export const PAYROLL_GENERATE_ALL = "all";

const SHIFT_TYPE_TO_TAB = {
  full_time: "fulltime",
  fulltime: "fulltime",
  morning: "morning",
  afternoon: "afternoon",
  evening: "afternoon",
  night: "night",
};

const mapEarningsRoles = (roles = [], idPrefix) =>
  roles.map((role, index) => ({
    id: `${idPrefix}-${index}`,
    rank: index + 1,
    name: role.role,
    days: role.number_of_days,
    ratePerDay: role.expected_payroll,
    total: role.total_expected_payroll,
    shiftType: SHIFT_TYPE_TO_TAB[role.shift_type] || null,
  }));

export const mapPayrollAdjustmentEntries = (
  rows = [],
  { lang, isReward, autoDeductionReason } = {},
) =>
  rows.map((row) => {
    const isAuto = row.adjustment_type === "automatic_deduction";
    const amount = Number(row.amount) || 0;
    const reasonLabel = row.reason
      ? lang === "ar"
        ? row.reason.title_ar
        : row.reason.title_en
      : isAuto
        ? autoDeductionReason
        : "";

    return {
      id: row.id,
      type: isAuto ? "auto" : "manual",
      reason: reasonLabel,
      amount: isReward ? amount : -Math.abs(amount),
      canRevert: !isAuto,
    };
  });

// Negating the sum directly yields -0 when there is nothing to deduct, which
// leaks into anything that formats or serialises the value.
const sumDeductions = (row) => {
  const total =
    Math.abs(row.auto_deduction || 0) + Math.abs(row.manual_deduction || 0);
  return total === 0 ? 0 : -total;
};

export const mapSinglePayroll = (row) => ({
  mode: "single",
  applicant: {
    id: row.id,
    name: row.name,
    image: row.image,
    stcNumber: row.stc_number,
    idNumber: row.id_number,
    nationality: row.nationality,
  },
  displayName: row.name,
  period: { startDate: "", endDate: "" },
  summary: {
    baseEarnings: row.total_payroll_amount || 0,
    deductions: sumDeductions(row),
    rewards: row.reward || 0,
    training: row.total_training_payroll_amount || 0,
    netPay: row.total_amount || 0,
  },
  baseEarnings: {
    total: row.total_payroll_amount || 0,
    roleCount: (row.roles || []).length,
    totalDays: row.total_days || 0,
    roles: mapEarningsRoles(row.roles, "role"),
  },
  training: {
    total: row.total_training_payroll_amount || 0,
    roleCount: (row.training_roles || []).length,
    totalDays: row.total_training_days || 0,
    roles: mapEarningsRoles(row.training_roles, "training"),
  },
  deductions: {
    total: sumDeductions(row),
    entryCount: 0,
    entries: [],
  },
  rewards: {
    total: row.reward || 0,
    entryCount: 0,
    entries: [],
  },
});

const buildRoleSummary = (row) => `${(row.roles || []).length} Roles`;

const normalizeReason = (reason) =>
  !reason || String(reason).trim().toUpperCase() === "N/A" ? "-" : reason;

export const mapBulkPayrollReport = (payrolls = [], totals = {}) => ({
  mode: "bulk",
  applicantCount: payrolls.length,
  period: { startDate: "", endDate: "" },
  summary: {
    totalDeductions: totals.totalDeductions ? -Math.abs(totals.totalDeductions) : 0,
    totalRewards: totals.totalRewards || 0,
    totalNetPay: totals.totalNetPay || 0,
  },
  applicants: payrolls.map((row) => ({
    id: row.id,
    name: row.name,
    displayName: row.name,
    image: row.image,
    roleSummary: buildRoleSummary(row),
    roles: mapEarningsRoles(row.roles, "role"),
    days: row.total_days || 0,
    shiftPay: row.total_payroll_amount || 0,
    autoDeduction: -Math.abs(row.auto_deduction || 0),
    manualDeduction: -Math.abs(row.manual_deduction || 0),
    manualDeductionReason: normalizeReason(row.manual_deduction_reason),
    rewards: row.reward || 0,
    rewardReason: normalizeReason(row.reward_reason),
    trainingDays: row.total_training_days || 0,
    trainingPay: row.total_training_payroll_amount || 0,
    trainingRoles: mapEarningsRoles(row.training_roles, "training"),
    totalAmount: row.total_amount || 0,
    stcNumber: row.stc_number,
    idNumber: row.id_number,
    nationality: row.nationality,
  })),
});

const SHIFT_TAB_ORDER = ["fulltime", "morning", "afternoon", "night"];

/** Admin and Company return this figure differently: a plain number, or `{ source, parsedValue }`. */
const getRatePerDay = (value) => {
  if (value && typeof value === "object") return value.parsedValue ?? 0;
  return value ?? 0;
};

/**
 * Role/training per-day rates come from the event's own role shifts, not the
 * payroll list contract — dedupe by role name per shift so the bulk report's
 * rate legend reflects what the event actually pays, not fabricated data.
 */
export const buildEventRoleRates = (event) => {
  const roleRatesByShift = {};
  const trainingRatesByShift = {};
  const shiftsSeen = new Set();

  (event?.event_roles || []).forEach((eventRole) => {
    const roleName = eventRole?.role?.name;
    if (!roleName) return;

    // Admin returns `event_role_shifts_attributes`, Company returns `event_role_shifts`.
    const shifts =
      eventRole.event_role_shifts_attributes || eventRole.event_role_shifts || [];

    shifts.forEach((shift) => {
      const shiftTab = SHIFT_TYPE_TO_TAB[shift?.shift_type];
      if (!shiftTab) return;
      shiftsSeen.add(shiftTab);

      roleRatesByShift[shiftTab] = roleRatesByShift[shiftTab] || [];
      if (!roleRatesByShift[shiftTab].some((rate) => rate.name === roleName)) {
        roleRatesByShift[shiftTab].push({
          id: `${shiftTab}-${roleName}`,
          name: roleName,
          rate: getRatePerDay(shift.payroll_amount_per_day),
        });
      }

      if (shift.training) {
        trainingRatesByShift[shiftTab] = trainingRatesByShift[shiftTab] || [];
        if (!trainingRatesByShift[shiftTab].some((rate) => rate.name === roleName)) {
          trainingRatesByShift[shiftTab].push({
            id: `${shiftTab}-training-${roleName}`,
            name: roleName,
            rate: getRatePerDay(shift.training.payroll_amount_per_day),
          });
        }
      }
    });
  });

  return {
    shifts: SHIFT_TAB_ORDER.filter((tab) => shiftsSeen.has(tab)),
    roleRatesByShift,
    trainingRatesByShift,
  };
};
